import {
  Button, Card, DateInput, ErrorState, Field, Input, LARGEST_PAGE, Notice, Permissions, Row, Select, Stack, Textarea,
  invalidDateIn, useApi, useI18n, usePermission,
  type ChildDetailResponse, type DiaperLogChoice, type Gender,
} from '@daghfal/core';

const DIAPER_CHOICES: DiaperLogChoice[] = ['Class', 'On', 'Off'];
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/** The profile and every list it appears on, after a change to the child (FR-ADM-06, FR-ADM-07). */
function useChildChanged(childId: number) {
  const queryClient = useQueryClient();
  return () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['child', childId] }),
    queryClient.invalidateQueries({ queryKey: ['children'] }),
    queryClient.invalidateQueries({ queryKey: ['classes'] }),
  ]);
}

/**
 * Correcting who the child is (FR-ADM-06): names, the name they are called by, date of birth, gender, when the current
 * enrolment began and whether nappies are logged for them whatever their class says (FR-DAY-08), behind
 * `children.update`. What is unchanged is sent as nothing, so the record keeps it; that matters most for the enrolment
 * date, which the API refuses to move across a closed term.
 */
export function ChildDetailsCard({ child }: { child: ChildDetailResponse }) {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const canUpdate = usePermission(Permissions.children.update);
  const changed = useChildChanged(child.id);
  const [editing, setEditing] = useState(false);

  const [firstName, setFirstName] = useState(child.firstName);
  const [lastName, setLastName] = useState(child.lastName);
  const [preferredName, setPreferredName] = useState(child.preferredName ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(child.dateOfBirth);
  const [gender, setGender] = useState<Gender>(child.gender);
  const [enrollmentDate, setEnrollmentDate] = useState(child.enrollmentDate);
  const [diaperLog, setDiaperLog] = useState<DiaperLogChoice>(child.diaperLog);

  const active = child.status === 'Active';

  const open = () => {
    setFirstName(child.firstName);
    setLastName(child.lastName);
    setPreferredName(child.preferredName ?? '');
    setDateOfBirth(child.dateOfBirth);
    setGender(child.gender);
    setEnrollmentDate(child.enrollmentDate);
    setDiaperLog(child.diaperLog);
    save.reset();
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: () => api.children.update(child.id, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      // An empty name clears it: the child is called by their first name again.
      preferredName: preferredName.trim(),
      dateOfBirth,
      gender,
      enrollmentDate: enrollmentDate !== child.enrollmentDate ? enrollmentDate : null,
      diaperLog: diaperLog !== child.diaperLog ? diaperLog : null,
    }),
    onSuccess: async () => {
      await changed();
      setEditing(false);
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget) || !firstName.trim() || !lastName.trim() || !dateOfBirth || !enrollmentDate) return;
    save.mutate();
  };

  if (!editing) {
    return (
      <Card
        title={t('child.details')}
        action={canUpdate && <Button variant="secondary" onClick={open}>{t('action.edit')}</Button>}
      >
        <Stack tight>
          <span>{child.firstName} {child.lastName}{child.preferredName ? ` (${child.preferredName})` : ''}</span>
          <span className="dg-card__hint">{formatDate(child.dateOfBirth)} · {t(`gender.${child.gender}`)}</span>
          <span className="dg-card__hint">
            {t(child.logsDiapers ? 'child.diaperLog.yes' : 'child.diaperLog.no')}
            {child.diaperLog === 'Class' && ` · ${t('child.diaperLog.Class')}`}
          </span>
        </Stack>
      </Card>
    );
  }

  return (
    <Card title={t('child.details')}>
      <form noValidate onSubmit={submit}>
        <Stack>
          <Row className="dg-row--wrap">
            <Field label={t('children.new.firstName')} required>
              {(props) => <Input {...props} value={firstName} onChange={(event) => setFirstName(event.target.value)} />}
            </Field>
            <Field label={t('children.new.lastName')} required>
              {(props) => <Input {...props} value={lastName} onChange={(event) => setLastName(event.target.value)} />}
            </Field>
          </Row>
          <Field label={t('children.new.preferredName')} hint={t('children.new.preferredName.hint')}>
            {(props) => <Input {...props} value={preferredName} onChange={(event) => setPreferredName(event.target.value)} />}
          </Field>
          <Row className="dg-row--wrap">
            <Field label={t('children.new.dob')} required>
              {(props) => <DateInput {...props} value={dateOfBirth} onChange={setDateOfBirth} />}
            </Field>
            <Field label={t('children.new.gender')}>
              {(props) => (
                <Select {...props} value={gender} onChange={(event) => setGender(event.target.value as Gender)}>
                  <option value="Female">{t('gender.Female')}</option>
                  <option value="Male">{t('gender.Male')}</option>
                </Select>
              )}
            </Field>
          </Row>
          {active && (
            <Field label={t('child.enrolled')} hint={t('child.enrolled.hint')} required>
              {(props) => <DateInput {...props} value={enrollmentDate} onChange={setEnrollmentDate} />}
            </Field>
          )}
          <Field label={t('child.diaperLog')} hint={t('child.diaperLog.hint')}>
            {(props) => (
              <Select {...props} value={diaperLog} onChange={(event) => setDiaperLog(event.target.value as DiaperLogChoice)}>
                {DIAPER_CHOICES.map((choice) => <option key={choice} value={choice}>{t(`child.diaperLog.${choice}`)}</option>)}
              </Select>
            )}
          </Field>
          {save.isError && <ErrorState error={save.error} />}
          <Row>
            <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')}>{t('action.save')}</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

/**
 * Moving a child to another class from a date (FR-ADM-07), behind `children.transfer`.
 *
 * The days before it keep the class they were recorded in (`ClassHistory`), and a date in a closed term is refused
 * with the API's own words (Q34). A class that goes over its capacity takes the child and says so.
 */
export function TransferCard({ child }: { child: ChildDetailResponse }) {
  const api = useApi();
  const { t, formatNumber } = useI18n();
  const canTransfer = usePermission(Permissions.children.transfer);
  const changed = useChildChanged(child.id);
  const [open, setOpen] = useState(false);
  const [toClassId, setToClassId] = useState<number | ''>('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active', pageSize: LARGEST_PAGE }],
    queryFn: () => api.classes.list({ status: 'Active', pageSize: LARGEST_PAGE }),
    enabled: open,
  });

  const transfer = useMutation({
    mutationFn: () => api.children.transfer(child.id, {
      toClassId: Number(toClassId),
      effectiveDate: effectiveDate || null,
      reason: reason.trim() || null,
    }),
    onSuccess: async () => {
      await changed();
      setOpen(false);
      setReason('');
      setEffectiveDate('');
    },
  });

  if (!canTransfer || child.status !== 'Active') return null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget) || toClassId === '') return;
    transfer.mutate();
  };

  const moved = transfer.data;

  return (
    <Card
      title={t('child.transfer')}
      action={!open && <Button variant="secondary" onClick={() => { transfer.reset(); setOpen(true); }}>{t('child.transfer')}</Button>}
    >
      <Stack>
        {moved && !open && (
          <Notice tone={moved.overCapacity ? 'warning' : 'success'}>
            {moved.overCapacity
              ? t('child.transfer.overCapacity', {
                  occupancy: formatNumber(moved.occupancy), capacity: formatNumber(moved.capacity),
                })
              : t('child.transfer.done')}
          </Notice>
        )}
        {open && (
          <form noValidate onSubmit={submit}>
            <Stack>
              <p className="dg-card__hint">{t('child.transfer.hint')}</p>
              <Field label={t('child.transfer.to')} required>
                {(props) => (
                  <Select
                    {...props}
                    value={toClassId}
                    onChange={(event) => setToClassId(event.target.value === '' ? '' : Number(event.target.value))}
                  >
                    <option value="">—</option>
                    {(classes.data?.items ?? []).filter((klass) => klass.id !== child.classId).map((klass) => (
                      <option key={klass.id} value={klass.id}>{klass.name}</option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('child.transfer.date')} hint={t('child.transfer.date.hint')}>
                {(props) => <DateInput {...props} value={effectiveDate} onChange={setEffectiveDate} />}
              </Field>
              <Field label={t('child.transfer.reason')}>
                {(props) => <Textarea {...props} rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />}
              </Field>
              {transfer.isError && <ErrorState error={transfer.error} />}
              <Row>
                <Button type="submit" disabled={toClassId === ''} busy={transfer.isPending} busyLabel={t('action.saving')}>
                  {t('child.transfer.submit')}
                </Button>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t('action.cancel')}</Button>
              </Row>
            </Stack>
          </form>
        )}
      </Stack>
    </Card>
  );
}
